import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CircularProgress from '@mui/material/CircularProgress';
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Typography, Box } from '@mui/material';
import CardHeader from '@mui/material/CardHeader';
import { useWidgetData } from './useWidgetData';
import { WidgetConfig } from './widget';
import { WidgetEditControls } from './WidgetEditControls';

interface MetricWidgetProps {
  widget: WidgetConfig;
  onEdit: (widget: WidgetConfig) => void;
  onRemove: (widgetId: string) => void;
  onSave?: (widget: WidgetConfig) => void;
  hasUnsavedChanges?: boolean;
  isEditing: boolean;
}

export const MetricWidget = ({
  widget,
  onEdit,
  onRemove,
  onSave,
  hasUnsavedChanges,
  isEditing,
}: MetricWidgetProps) => {
  const { data, isLoading, error, refetch } = useWidgetData(widget);

  const getMetricFromData = () => {
    if (!data?.data || !widget.chartConfiguration?.xAxis?.columnName) {
      return null;
    }

    const { columnName } = widget.chartConfiguration.xAxis;
    let dataArray: any[] = [];


    if (data.data.Results && Array.isArray(data.data.Results)) {
      dataArray = data.data.Results;
    } else if (Array.isArray(data.data)) {
      dataArray = data.data;
    } else {
      return null;
    }

    if (dataArray.length === 0) {
      return null;
    }


    const getValueFromRecord = (record: any, column: string) => {

      if (record && record[column] !== undefined && record[column] !== null) {
        return record[column];
      }


      if (record && record.telemetry && typeof record.telemetry === 'object') {
        if (record.telemetry[column] !== undefined && record.telemetry[column] !== null) {
          return record.telemetry[column];
        }
      }

      return undefined;
    };

    const sortedData = dataArray.sort((a, b) => {
      const timeA = a.timestamp || a.TimeGenerated || a.time || 0;
      const timeB = b.timestamp || b.TimeGenerated || b.time || 0;
      return new Date(timeA).getTime() - new Date(timeB).getTime();
    });

    const latestRecord = sortedData[sortedData.length - 1];
    const value = getValueFromRecord(latestRecord, columnName);

    if (value === undefined || value === null) {
      return null;
    }

    let change = 0;
    if (sortedData.length > 1) {
      const previousRecord = sortedData[sortedData.length - 2];
      const previousValue = getValueFromRecord(previousRecord, columnName);

      if (previousValue !== undefined && previousValue !== null && previousValue !== 0) {
        const numValue = typeof value === 'number' ? value : parseFloat(value);
        const numPreviousValue = typeof previousValue === 'number' ? previousValue : parseFloat(previousValue);

        if (!Number.isNaN(numValue) && !Number.isNaN(numPreviousValue) && numPreviousValue !== 0) {
          change = ((numValue - numPreviousValue) / numPreviousValue) * 100;
        }
      }
    }

    const numericValue = typeof value === 'number' ? value : parseFloat(value);

    if (Number.isNaN(numericValue)) {
      return null;
    }

    return {
      value: numericValue,
      change,
      unit: '',
    };
  };

  const metric = getMetricFromData();
  const isPositive = metric ? metric.change >= 0 : false;

  return (
    <Box>
      <WidgetEditControls
        widget={widget}
        onEdit={onEdit}
        onRemove={onRemove}
        onSave={onSave}
        hasUnsavedChanges={hasUnsavedChanges}
        isVisible={isEditing}
      />

      <Card
        sx={{
          bgcolor: 'white',
          border: '1px solid #d1d5db',
          color: 'black',
        }}
      >
        <CardHeader
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            '& .MuiCardHeader-title': { fontWeight: 600, fontSize: 18 },
          }}
          title={
            <Typography variant="h6" color="text.primary">
              {widget.title}
            </Typography>
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isLoading && (
                <CircularProgress size={20} sx={{ color: '#60a5fa', mr: 1 }} />
              )}
              <button
                onClick={() => refetch()}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <AutorenewIcon sx={{ fontSize: 20 }} />
              </button>
            </Box>
          }
        />
        <CardContent sx={{ pt: 0, pb: 0, minHeight: 'auto' }}>
          {(() => {
            if (error) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 128,
                    color: '#f87171',
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 28, mr: 1 }} />
                  <span>Error loading data</span>
                </Box>
              );
            }

            if (!metric) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 128,
                    color: '#9ca3af',
                  }}
                >
                  <Typography variant="body2">
                    {(() => {
                      if (isLoading) return 'Loading data...';
                      return 'No data available. Configure the widget and execute a query to display metric data.';
                    })()}
                  </Typography>
                </Box>
              );
            }

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ pt: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 'medium',
                      color: '#6b7280',
                      mb: 1
                    }}
                  >
                    {widget.chartConfiguration?.xAxis?.columnName || widget.chartConfiguration?.xAxis?.label || 'Metric'}
                  </Typography>
                  <Box sx={{ fontSize: 32, fontWeight: 'bold', color: '#60a5fa' }}>
                    {metric.value?.toLocaleString()} {metric.unit}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isPositive ? (
                    <TrendingUp sx={{ fontSize: 32, color: '#10b981' }} />
                  ) : (
                    <TrendingDown sx={{ fontSize: 32, color: '#f87171' }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: 20,
                      color: isPositive ? '#10b981' : '#f87171',
                    }}
                  >
                    {isPositive ? '+' : ''}
                    {metric.change?.toFixed(2)}%
                  </Typography>
                  <Typography sx={{ fontSize: 20, color: '#9ca3af' }}>
                    vs last period
                  </Typography>
                </Box>
              </Box>
            );
          })()}
          <Typography
            variant="subtitle2"
            sx={{
              color: '#9ca3af',
              mt: 1,
            }}
          >
            Type: {widget.chartType} | Refresh: {widget.dataSource.refreshRate}s | Last updated:{' '}
            {data?.timestamp
              ? new Date(data.timestamp).toLocaleTimeString()
              : 'Never'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
